import { SentryFetchHttpHandler } from "@amzn/sentry-fetch-http-handler";
import { PiezoNotaryClerkPermitClient, ListPermitsCommand, ListDecksCommand } from '@amzn/piezo-notaryclerk-typescript-client';
import { bytesToEncoded } from '../byteUtils.js';
import { PIEZO_ENDPOINTS } from '../__generated__/piezo-endpoints.js';
import { createSigningNameOverrideProvider } from './signingUtils.js';
const NOTARY_RIP_STAGES = ['gamma', 'prod'];
const NOTARY_REGIONS = [
    'ap-northeast-1',
    'us-east-2',
    'eu-central-1',
    'us-west-2',
    'eu-west-3',
];
export class PiezoNotaryThickClient {
    plenumClient;
    notaryClients;
    encoder;
    constructor(plenumClient, props) {
        this.plenumClient = plenumClient;
        this.encoder = new TextEncoder();
        this.notaryClients = {};
        if (props.clientOverride) {
            const client = this.getNotaryClient(props.authType, props.clientOverride.region, props.clientOverride.endpoint, props.credentials);
            this.notaryClients[props.clientOverride.endpoint] = client;
        }
        else if (props.region && NOTARY_RIP_STAGES.includes(props.stage)) {
            // Use build-time generated RIP endpoints for the specified region
            const region = props.region;
            const generatedConfig = PIEZO_ENDPOINTS[props.stage]?.[props.authType]?.[region];
            if (generatedConfig && generatedConfig.notaryClerks.length > 0) {
                for (const clerk of generatedConfig.notaryClerks) {
                    this.notaryClients[clerk.url] = this.getNotaryClient(props.authType, region, clerk.url, props.credentials, clerk.signingName);
                }
            }
            else {
                throw new Error(`[Notary] No generated endpoints found for ${props.stage}/${props.authType}/${region}. Ensure that your version set has latest RIP data.`);
            }
        }
        else {
            // Default: static endpoint templates (no region specified)
            this.getRegions(props.stage).forEach((region) => {
                const endpoint = this.getEndpoint(props.authType, props.stage, region);
                this.notaryClients[endpoint] = this.getNotaryClient(props.authType, region, endpoint, props.credentials);
            });
        }
    }
    /**
     * Lists permits across all Notary clerks.
     *
     * @returns A mapping from policy name to all of the permits for that policy.
     */
    async listAllPermits() {
        const permits = await Promise.all(Object.entries(this.notaryClients).map(async ([clerkEndpoint, client]) => {
            const permits = await this.listPermits(client);
            return await Promise.all(permits.map(async (permit) => {
                return {
                    clerkEndpoint,
                    permit,
                    policyName: await this.parsePolicyNameFromPermit(permit)
                };
            }));
        }));
        return permits.flat().reduce((mappings, permitInfo) => {
            const current = mappings.get(permitInfo.policyName);
            if (current) {
                current.push(permitInfo);
                mappings.set(permitInfo.policyName, current);
            }
            else {
                mappings.set(permitInfo.policyName, [permitInfo]);
            }
            return mappings;
        }, new Map());
    }
    async listPermits(client) {
        let nextToken = undefined;
        const output = [];
        do {
            const resp = await client.send(new ListPermitsCommand({ nextToken }));
            output.push(...resp.permits);
            nextToken = resp.nextToken;
        } while (nextToken);
        return output;
    }
    /**
     * Parses the policy name out of a given permit.
     */
    async parsePolicyNameFromPermit(permit) {
        const parsedPermit = await this.plenumClient.parsePlenumDocument({
            document: bytesToEncoded(permit.permit)
        });
        const permitJson = JSON.parse(window.atob(parsedPermit.data));
        return permitJson.PolicyName;
    }
    /**
     * Lists the decks across all of the notary clerks.
     *
     * @returns A mapping from clerk (endpoint) to their decks.
     */
    async listAllClerkDecks() {
        const endpointToDeckMaps = await Promise.all(Object.entries(this.notaryClients).map(async ([endpoint, client]) => {
            let nextToken;
            const decks = [];
            do {
                const resp = await client.send(new ListDecksCommand({ nextToken }));
                decks.push(...resp.decks.map(deck => deck.deck));
                nextToken = resp.nextToken;
            } while (nextToken);
            const output = new Map();
            output.set(endpoint, decks);
            return output;
        }));
        return endpointToDeckMaps.reduce((acc, cur) => {
            cur.forEach((decks, endpoint) => acc.set(endpoint, decks));
            return acc;
        }, new Map());
    }
    getRegions(stage) {
        switch (stage.toLowerCase()) {
            case 'beta':
                return NOTARY_REGIONS;
            case 'gamma':
                return NOTARY_REGIONS;
            case 'prod':
                return NOTARY_REGIONS;
            default:
                throw `unsupported stage ${stage}! Only beta, gamma, and prod supported`;
        }
    }
    getEndpoint(authType, stage, region) {
        return `https://notary-clerk-a.${stage}.${authType}.${region}.notary.piezo.aws.dev`;
    }
    getNotaryClient(authType, region, endpoint, credentials, signingName) {
        if (authType === "iam") {
            return new PiezoNotaryClerkPermitClient({
                endpoint,
                region,
                ...(signingName && { httpAuthSchemeProvider: createSigningNameOverrideProvider(signingName) }),
            });
        }
        else if (authType === "midway") {
            return new PiezoNotaryClerkPermitClient({
                endpoint,
                region: 'ignored',
                credentials: { accessKeyId: 'ignored', secretAccessKey: 'ignored' },
                requestHandler: new SentryFetchHttpHandler()
            });
        }
        else {
            throw `Unknown/unsupported AuthType: ${authType}`;
        }
    }
}
